import { IssueCommentWithCreatedBy } from "@boark/shared/types/issue-comment-with-created-by";
import { useState } from "react";

import { useGetAuthContext } from "../queries/get-auth-context";
import {
  useCreateIssueComment,
  useDeleteIssueComment,
  useGetIssueComments,
  useUpdateIssueComment,
} from "../queries/issue-comment";
import { useLocale } from "../stores/locale";
import { formatDateTime } from "../utils/date";
import { Button } from "./button";
import { Confirm } from "./confirm";

export function IssueComments({
  issueIdentifier,
}: {
  issueIdentifier: string | number;
}) {
  const { locale: t } = useLocale();
  const { data: authContext } = useGetAuthContext();
  const { data: comments } = useGetIssueComments(issueIdentifier);
  const createComment = useCreateIssueComment(issueIdentifier);
  const [newComment, setNewComment] = useState("");

  const handleAddComment = async () => {
    const trimmed = newComment.trim();
    if (trimmed === "") {
      return;
    }
    await createComment.mutateAsync({ comment: trimmed });
    setNewComment("");
  };

  return (
    <div className="flex flex-col gap-4">
      <h2 className="text-lg font-bold m-0">{t.issue.comments}</h2>
      <form
        className="flex flex-col gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          void handleAddComment();
        }}
      >
        <textarea
          className="w-full border-1 border-gray-300 rounded-xs p-2 min-h-[4.5rem] bg-white disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
          disabled={createComment.isPending}
          placeholder={t.issue.commentPlaceholder}
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
        />
        <div className="self-end">
          <Button
            disabled={newComment.trim() === ""}
            loading={createComment.isPending}
            type="submit"
          >
            {t.issue.commentAdd}
          </Button>
        </div>
      </form>
      {comments && comments.length > 0 ? (
        <ul className="flex flex-col gap-4 list-none m-0 p-0">
          {comments.map((comment) => (
            <li key={comment.id}>
              <IssueCommentRow
                authUserId={authContext?.id}
                comment={comment}
                issueIdentifier={issueIdentifier}
              />
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-gray-500 m-0">{t.issue.commentsEmpty}</p>
      )}
    </div>
  );
}

function IssueCommentRow({
  comment,
  issueIdentifier,
  authUserId,
}: {
  comment: IssueCommentWithCreatedBy;
  issueIdentifier: string | number;
  authUserId?: string;
}) {
  const { locale: t, get: getLocale } = useLocale();
  const updateComment = useUpdateIssueComment(issueIdentifier);
  const deleteComment = useDeleteIssueComment(issueIdentifier);
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(comment.comment);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const canModify = authUserId === comment.createdBy.id;
  const wasEdited =
    new Date(comment.updatedAt).getTime() >
    new Date(comment.createdAt).getTime();

  const startEditing = () => {
    setEditValue(comment.comment);
    setEditing(true);
  };

  const abortEditing = () => {
    setEditValue(comment.comment);
    setEditing(false);
  };

  const confirmEdit = async () => {
    const trimmed = editValue.trim();
    if (trimmed === "") {
      return;
    }
    await updateComment.mutateAsync({
      commentId: comment.id,
      comment: { comment: trimmed },
    });
    setEditing(false);
  };

  return (
    <div className="flex flex-col gap-2 border border-gray-200 rounded-xs p-3">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-sm text-gray-600">
        <span className="font-medium text-gray-900">
          {comment.createdBy.email}
        </span>
        <span>{formatDateTime(comment.createdAt)}</span>
        {wasEdited && (
          <span className="italic">({t.issue.commentEdited})</span>
        )}
      </div>
      {editing ? (
        <div className="flex flex-col gap-2">
          <textarea
            className="w-full border-1 border-gray-300 rounded-xs p-2 min-h-[4.5rem] bg-white disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
            disabled={updateComment.isPending}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
          />
          <div className="flex gap-2">
            <Button
              disabled={editValue.trim() === ""}
              loading={updateComment.isPending}
              type="button"
              onClick={() => void confirmEdit()}
            >
              {t.page.save}
            </Button>
            <Button
              disabled={updateComment.isPending}
              severity="secondary"
              type="button"
              onClick={abortEditing}
            >
              {t.cancel}
            </Button>
          </div>
        </div>
      ) : (
        <p className="m-0 whitespace-pre-wrap">{comment.comment}</p>
      )}
      {canModify && !editing && (
        <div className="flex gap-2">
          <Button severity="secondary" size="sm" type="button" onClick={startEditing}>
            {t.page.edit}
          </Button>
          <Button
            severity="danger"
            size="sm"
            type="button"
            onClick={() => setDeleteConfirmOpen(true)}
          >
            {t.issue.commentDelete}
          </Button>
        </div>
      )}
      {deleteConfirmOpen && (
        <Confirm
          header={getLocale("issue.commentConfirmDelete")}
          loading={deleteComment.isPending}
          onClose={() => setDeleteConfirmOpen(false)}
          onConfirm={() => {
            void (async () => {
              await deleteComment.mutateAsync(comment.id);
              setDeleteConfirmOpen(false);
            })();
          }}
        />
      )}
    </div>
  );
}
